import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const PLESSI_COLS = ["INFANZIA", "PRIMARIA", "SECONDARIA", "COMUNITA", "ALTRO"];
const fmt = n => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const EMPTY = () => ({ INFANZIA:0, PRIMARIA:0, SECONDARIA:0, COMUNITA:0, ALTRO:0, tot:0 });
const nodeCode = (v) => {
  if (!v) return "";
  if (v.livello === 1) return `${v.c0}`;
  if (v.livello === 2) return `${v.c0}.${v.c1}`;
  if (v.livello === 3) return `${v.c0}.${v.c1}.${v.c2}`;
  return `${v.c0}.${v.c1 ?? "-"}.${v.c3 ?? "-"}`; // foglia / sottoconto
};

export default function PrimaNotaBilancioPage() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const isoToday = today.toISOString().slice(0, 10);
  const isoStart = `${yyyy}-01-01`;
  const [dataDal, setDataDal] = useState(isoStart);
  const [dataAl, setDataAl] = useState(isoToday);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [righe, setRighe] = useState(0);
  const [piano, setPiano] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [filtroSezione, setFiltroSezione] = useState("all"); // 'all' | 1 (COSTI) | 2 (RICAVI)
  const [mappingByConto, setMappingByConto] = useState({});
  const [movimentiConto, setMovimentiConto] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      const params = { source: "fatture", dual: true };
      if (dataDal) params.data_dal = dataDal;
      if (dataAl) params.data_al = dataAl;
      const [r, p, m] = await Promise.all([
        api.get("/pn/report/bilancio", { params }),
        piano.length === 0 ? api.get("/pn/bilancio-voci") : Promise.resolve({ data: { data: piano } }),
        api.get("/pn/mapping/ricavi"),
      ]);
      setData(r.data.data || []);
      setTotals(r.data.totals || null);
      setRighe(r.data.righe || 0);
      if (p.data && p.data.data) setPiano(p.data.data);
      // mappa id_conto → array di causali
      const mapByConto = {};
      (m.data?.data || []).filter(x => x.id_conto).forEach(x => {
        if (!mapByConto[x.id_conto]) mapByConto[x.id_conto] = [];
        mapByConto[x.id_conto].push({ cau_code: x.cau_code, cau_des: x.cau_des });
      });
      setMappingByConto(mapByConto);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [dataDal, dataAl]);

  const pivotByConto = useMemo(() => {
    const m = {};
    data.forEach(r => { m[r.id_conto] = r; });
    return m;
  }, [data]);

  // 🆕 Feb 2026: totale "complessivo" = Ricavi − Costi (utile netto), non somma cieca.
  // Calcoliamo per sezione (Ricavi c0=2, Costi c0=1) sia per Fatture che Op. Extra.
  const sezioneRicaviC0 = useMemo(() => {
    const s = piano.filter(v => v.livello === 1);
    return s.find(v => (v.descrizione || "").toLowerCase().includes("ricav"))?.c0 ?? 2;
  }, [piano]);
  const totalsBySezione = useMemo(() => {
    const acc = { ricavi: EMPTY(), costi: EMPTY(), ricavi_extra: EMPTY(), costi_extra: EMPTY() };
    data.forEach(r => {
      const isRicavo = Number(r.c0) === Number(sezioneRicaviC0);
      const fat = r.fatture || r;
      const ext = r.extra || {};
      const bucketF = isRicavo ? acc.ricavi : acc.costi;
      const bucketE = isRicavo ? acc.ricavi_extra : acc.costi_extra;
      for (const k of Object.keys(EMPTY())) {
        bucketF[k] = (bucketF[k] || 0) + (fat[k] || 0);
        bucketE[k] = (bucketE[k] || 0) + (ext[k] || 0);
      }
    });
    // Netto = Ricavi − Costi
    const nettoF = EMPTY();
    const nettoE = EMPTY();
    for (const k of Object.keys(EMPTY())) {
      nettoF[k] = (acc.ricavi[k] || 0) - (acc.costi[k] || 0);
      nettoE[k] = (acc.ricavi_extra[k] || 0) - (acc.costi_extra[k] || 0);
    }
    return { ...acc, netto_fat: nettoF, netto_ext: nettoE };
  }, [data, sezioneRicaviC0]);

  const tree = useMemo(() => {
    const byL1 = {};
    piano.forEach(v => {
      if (v.c0 == null) return;
      if (!byL1[v.c0]) byL1[v.c0] = { node: null, children: {}, leaves: [] };
      if (v.livello === 1) byL1[v.c0].node = v;
      else if (v.livello === 2 && v.c1 != null) {
        if (!byL1[v.c0].children[v.c1]) byL1[v.c0].children[v.c1] = { node: null, children: {}, leaves: [] };
        byL1[v.c0].children[v.c1].node = v;
      } else if (v.livello === 3 && v.c1 != null && v.c2 != null) {
        if (!byL1[v.c0].children[v.c1]) byL1[v.c0].children[v.c1] = { node: null, children: {}, leaves: [] };
        if (!byL1[v.c0].children[v.c1].children[v.c2]) byL1[v.c0].children[v.c1].children[v.c2] = { node: null, leaves: [] };
        byL1[v.c0].children[v.c1].children[v.c2].node = v;
      } else if (v.is_foglia) {
        if (v.c1 == null) {
          byL1[v.c0].leaves.push(v);
        } else {
          if (!byL1[v.c0].children[v.c1]) byL1[v.c0].children[v.c1] = { node: null, children: {}, leaves: [] };
          if (v.c2 == null) {
            byL1[v.c0].children[v.c1].leaves.push(v);
          } else {
            if (!byL1[v.c0].children[v.c1].children[v.c2]) byL1[v.c0].children[v.c1].children[v.c2] = { node: null, leaves: [] };
            byL1[v.c0].children[v.c1].children[v.c2].leaves.push(v);
          }
        }
      }
    });
    const sumInto = (a, b) => { for (const k of Object.keys(a)) a[k] = Number((a[k] + (b[k]||0)).toFixed(2)); };
    Object.values(byL1).forEach(n0 => {
      n0.tFat = EMPTY(); n0.tExt = EMPTY();
      Object.values(n0.children).forEach(n1 => {
        n1.tFat = EMPTY(); n1.tExt = EMPTY();
        // Foglie direttamente sotto la famiglia
        (n1.leaves || []).forEach(leaf => {
          const v = pivotByConto[leaf.id_conto];
          if (v) { sumInto(n1.tFat, v.fatture || EMPTY()); sumInto(n1.tExt, v.extra || EMPTY()); }
        });
        Object.values(n1.children).forEach(n2 => {
          n2.tFat = EMPTY(); n2.tExt = EMPTY();
          (n2.leaves || []).forEach(leaf => {
            const v = pivotByConto[leaf.id_conto];
            if (v) { sumInto(n2.tFat, v.fatture || EMPTY()); sumInto(n2.tExt, v.extra || EMPTY()); }
          });
          const own = pivotByConto[n2.node?.id_conto];
          if (own) { sumInto(n2.tFat, own.fatture || EMPTY()); sumInto(n2.tExt, own.extra || EMPTY()); }
          sumInto(n1.tFat, n2.tFat); sumInto(n1.tExt, n2.tExt);
        });
        const own = pivotByConto[n1.node?.id_conto];
        if (own) { sumInto(n1.tFat, own.fatture || EMPTY()); sumInto(n1.tExt, own.extra || EMPTY()); }
        sumInto(n0.tFat, n1.tFat); sumInto(n0.tExt, n1.tExt);
      });
      const own = pivotByConto[n0.node?.id_conto];
      if (own) { sumInto(n0.tFat, own.fatture || EMPTY()); sumInto(n0.tExt, own.extra || EMPTY()); }
    });
    return byL1;
  }, [piano, pivotByConto]);

  useEffect(() => {
    if (Object.keys(expanded).length === 0 && Object.keys(tree).length > 0) {
      const exp = {};
      Object.entries(tree).forEach(([k0, n0]) => {
        exp[k0] = true;
        Object.entries(n0.children).forEach(([k1, n1]) => {
          exp[`${k0}/${k1}`] = true;
          Object.keys(n1.children).forEach(k2 => { exp[`${k0}/${k1}/${k2}`] = true; });
        });
      });
      setExpanded(exp);
    }
  }, [tree, expanded]);

  const toggle = (k) => setExpanded(e => ({ ...e, [k]: !e[k] }));
  const isOpen = (k) => !!expanded[k];

  const eliminaConto = async (leaf) => {
    if (!window.confirm(`Eliminare il conto [${leaf.id_conto}] "${leaf.descrizione}"?\n\nL'operazione fallirà se ci sono righe contabili o mapping causali collegati.`)) return;
    try {
      await api.delete(`/pn/bilancio-voci/${leaf.id_conto}`);
      setPiano([]); // forza refetch
      await carica();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const FAT_COLOR = "#0d47a1";
  const EXT_COLOR = "#6a1b9a";

  return (
    <div>
      <div className="sw-page-title pn-bil-no-print">
        <h1>📊 Bilancio</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={carica} disabled={loading} data-testid="pn-bilancio-reload"
            style={{ padding: "5px 12px", background: FAT_COLOR, color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {loading ? "..." : "↻ Aggiorna"}
          </button>
          <button onClick={async () => {
            try {
              const r = await api.get("/pn/report/bilancio/xlsx", {
                params: { data_dal: dataDal || undefined, data_al: dataAl || undefined },
                responseType: "blob",
              });
              const url = window.URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement("a");
              a.href = url;
              a.download = `bilancio_${dataDal || "tutto"}_${dataAl || "tutto"}.xlsx`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) { alert("Errore esportazione: " + (e.response?.data?.detail || e.message)); }
          }} data-testid="pn-bilancio-export-xlsx"
            style={{ padding: "5px 12px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            📥 Excel
          </button>
          <button onClick={() => window.print()} data-testid="pn-bilancio-print"
            style={{ padding: "5px 12px", background: "#37474f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            🖨 Stampa
          </button>
        </div>
      </div>

      <div className="pn-bil-no-print" style={{ margin: "8px 20px 0", padding: 10, background: "white", border: "1px solid #ccc", display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Da data</div>
          <input type="date" value={dataDal} onChange={e => setDataDal(e.target.value)} data-testid="pn-bilancio-data-dal"
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>A data</div>
          <input type="date" value={dataAl} onChange={e => setDataAl(e.target.value)} data-testid="pn-bilancio-data-al"
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#444", display: "flex", gap: 16, alignItems: "center" }}>
          <span><b>{data.length}</b> conti</span>
          <span><b>{righe}</b> righe</span>
          {totals && (
            <>
              <span style={{ fontFamily: "monospace", fontSize: 13, color: FAT_COLOR, fontWeight: 800 }}>Σ Fatt € {fmt(totals.fatture?.tot)}</span>
              <span style={{ fontFamily: "monospace", fontSize: 13, color: EXT_COLOR, fontWeight: 800 }}>Σ Extra € {fmt(totals.extra?.tot)}</span>
            </>
          )}
        </div>
      </div>

      {/* Tab filtro Tutti / Costi / Ricavi */}
      {(() => {
        const sezioniRoots = piano.filter(v => v.livello === 1);
        const ricaviC0 = sezioniRoots.find(v => (v.descrizione || "").toLowerCase().includes("ricav"))?.c0 ?? 2;
        const costiC0 = sezioniRoots.find(v => v.c0 !== ricaviC0)?.c0 ?? 1;
        const tabs = [
          { k: "all",    l: "Tutti",   color: "#37474f" },
          { k: costiC0,  l: "Costi",   color: "#1565c0" },
          { k: ricaviC0, l: "Ricavi",  color: "#2e7d32" },
        ];
        return (
          <div className="pn-bil-no-print" style={{ margin: "0 20px", display: "flex", gap: 0, borderBottom: "2px solid #37474f" }}>
            {tabs.map(t => {
              const active = filtroSezione === t.k;
              return (
                <button key={t.k} onClick={() => setFiltroSezione(t.k)} data-testid={`pn-bil-tab-${t.k}`}
                  style={{
                    padding: "6px 18px",
                    background: active ? t.color : "#eee",
                    color: active ? "white" : "#666",
                    border: "none",
                    borderTop: active ? `2px solid ${t.color}` : "2px solid #ccc",
                    borderLeft: "1px solid #ccc",
                    borderRight: "1px solid #ccc",
                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}>{t.l}</button>
              );
            })}
          </div>
        );
      })()}

      <div className="pn-bil-print-area" style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", borderTop: 0, maxHeight: "70vh", overflow: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
          <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white", zIndex: 1 }}>
            {/* Riga 1: gruppi */}
            <tr>
              <th rowSpan={2} style={{ width: 55, textAlign: "center", background: "#37474f", color: "white", fontSize: 11, padding: "8px 4px" }}>COD.</th>
              <th rowSpan={2} style={{ minWidth: 340, background: "#37474f", color: "white", fontSize: 12, padding: "8px 10px" }}>MASTRO / CONTO / SOTTOCONTO</th>
              <th colSpan={6} style={{ background: FAT_COLOR, color: "white", textAlign: "center", borderRight: "3px solid white", fontSize: 13, fontWeight: 800, padding: "8px 6px", letterSpacing: 0.5 }}>📄 FATTURE / NOTE CREDITO (causali 1+2)</th>
              <th colSpan={6} style={{ background: EXT_COLOR, color: "white", textAlign: "center", fontSize: 13, fontWeight: 800, padding: "8px 6px", letterSpacing: 0.5 }}>📒 OPERAZIONI EXTRA (causale 3)</th>
            </tr>
            {/* Riga 2: plessi */}
            <tr>
              {PLESSI_COLS.map(p => <th key={`f-${p}`} style={{ width: 88, textAlign: "right", background: FAT_COLOR, color: "white", fontSize: 11, fontWeight: 700, padding: "6px 6px" }}>{p}</th>)}
              <th style={{ width: 100, textAlign: "right", background: "#073275", color: "white", borderRight: "3px solid white", fontSize: 11, fontWeight: 800, padding: "6px 6px" }}>TOT</th>
              {PLESSI_COLS.map(p => <th key={`e-${p}`} style={{ width: 88, textAlign: "right", background: EXT_COLOR, color: "white", fontSize: 11, fontWeight: 700, padding: "6px 6px" }}>{p}</th>)}
              <th style={{ width: 100, textAlign: "right", background: "#4a148c", color: "white", fontSize: 11, fontWeight: 800, padding: "6px 6px" }}>TOT</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(tree).length === 0 && <tr><td colSpan={14} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento piano dei conti...</td></tr>}
            {(() => {
              // RICAVI prima, poi COSTI
              const sezioniRoots = piano.filter(v => v.livello === 1);
              const ricaviC0 = sezioniRoots.find(v => (v.descrizione || "").toLowerCase().includes("ricav"))?.c0 ?? 2;
              return Object.keys(tree)
                .sort((a, b) => {
                  const ra = Number(a) === Number(ricaviC0) ? 0 : 1;
                  const rb = Number(b) === Number(ricaviC0) ? 0 : 1;
                  if (ra !== rb) return ra - rb;
                  return Number(a) - Number(b);
                })
                .filter(k0 => filtroSezione === "all" || Number(k0) === Number(filtroSezione));
            })().map(k0 => {
              const n0 = tree[k0];
              const k0open = isOpen(`${k0}`);
              return (
                <RenderNode key={k0} level={0} keyPath={`${k0}`} node={n0} open={k0open} onToggle={() => toggle(`${k0}`)}
                  tFat={n0.tFat} tExt={n0.tExt} fallbackLabel={`Sezione ${k0}`}>
                  {k0open && Object.keys(n0.children).sort((a,b) => Number(a) - Number(b)).map(k1 => {
                    const n1 = n0.children[k1];
                    const k1open = isOpen(`${k0}/${k1}`);
                    return (
                      <RenderNode key={k1} level={1} keyPath={`${k0}/${k1}`} node={n1} open={k1open} onToggle={() => toggle(`${k0}/${k1}`)}
                        tFat={n1.tFat} tExt={n1.tExt} fallbackLabel={`${k0}.${k1}`}>
                        {/* Foglie direttamente sotto la famiglia (c2=null) */}
                        {k1open && (n1.leaves||[]).sort((a,b) => (a.c3||0) - (b.c3||0)).map(leaf => (
                          <LeafRow key={leaf.id_conto} leaf={leaf} pivotByConto={pivotByConto} indent={50} onDelete={eliminaConto} mapping={mappingByConto} dataDal={dataDal} dataAl={dataAl} onOpenMovimenti={setMovimentiConto} />
                        ))}
                        {k1open && Object.keys(n1.children).sort((a,b) => Number(a) - Number(b)).map(k2 => {
                          const n2 = n1.children[k2];
                          const k2open = isOpen(`${k0}/${k1}/${k2}`);
                          return (
                            <RenderNode key={k2} level={2} keyPath={`${k0}/${k1}/${k2}`} node={n2} open={k2open} onToggle={() => toggle(`${k0}/${k1}/${k2}`)}
                              tFat={n2.tFat} tExt={n2.tExt} fallbackLabel={`${k0}.${k1}.${k2}`}>
                              {k2open && (n2.leaves||[]).sort((a,b) => (a.c3||0) - (b.c3||0)).map(leaf => (
                                <LeafRow key={leaf.id_conto} leaf={leaf} pivotByConto={pivotByConto} indent={64} onDelete={eliminaConto} mapping={mappingByConto} dataDal={dataDal} dataAl={dataAl} onOpenMovimenti={setMovimentiConto} />
                              ))}
                            </RenderNode>
                          );
                        })}
                      </RenderNode>
                    );
                  })}
                </RenderNode>
              );
            })}
          </tbody>
          {totals && (
            <tfoot>
              <tr style={{ background: "#1b5e20", color: "white", fontWeight: 700 }}>
                <td colSpan={2} style={{ textAlign: "right", padding: "6px 8px" }}>▸ TOTALE RICAVI</td>
                {PLESSI_COLS.map(p => <td key={`fr-${p}`} style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(totalsBySezione.ricavi[p])}</td>)}
                <td style={{ textAlign: "right", fontFamily: "monospace", background: "#2e7d32", borderRight: "3px solid white" }}>{fmt(totalsBySezione.ricavi.tot)}</td>
                {PLESSI_COLS.map(p => <td key={`er-${p}`} style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(totalsBySezione.ricavi_extra[p])}</td>)}
                <td style={{ textAlign: "right", fontFamily: "monospace", background: "#2e7d32" }}>{fmt(totalsBySezione.ricavi_extra.tot)}</td>
              </tr>
              <tr style={{ background: "#c62828", color: "white", fontWeight: 700 }}>
                <td colSpan={2} style={{ textAlign: "right", padding: "6px 8px" }}>▸ TOTALE COSTI</td>
                {PLESSI_COLS.map(p => <td key={`fc-${p}`} style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(totalsBySezione.costi[p])}</td>)}
                <td style={{ textAlign: "right", fontFamily: "monospace", background: "#b71c1c", borderRight: "3px solid white" }}>{fmt(totalsBySezione.costi.tot)}</td>
                {PLESSI_COLS.map(p => <td key={`ec-${p}`} style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(totalsBySezione.costi_extra[p])}</td>)}
                <td style={{ textAlign: "right", fontFamily: "monospace", background: "#b71c1c" }}>{fmt(totalsBySezione.costi_extra.tot)}</td>
              </tr>
              <tr style={{ background: "#263238", color: "white", fontWeight: 900, fontSize: 13 }} title="Utile / (Perdita) = Ricavi − Costi">
                <td colSpan={2} style={{ textAlign: "right", padding: "8px 8px" }}>= TOTALI COMPLESSIVI (Ricavi − Costi)</td>
                {PLESSI_COLS.map(p => {
                  const v = totalsBySezione.netto_fat[p];
                  return <td key={`fn-${p}`} style={{ textAlign: "right", fontFamily: "monospace", background: v >= 0 ? "#0d47a1" : "#880e4f", color: "white" }}>{fmt(v)}</td>;
                })}
                <td style={{ textAlign: "right", fontFamily: "monospace", background: (totalsBySezione.netto_fat.tot >= 0 ? "#1b5e20" : "#b71c1c"), borderRight: "3px solid white" }}>{fmt(totalsBySezione.netto_fat.tot)}</td>
                {PLESSI_COLS.map(p => {
                  const v = totalsBySezione.netto_ext[p];
                  return <td key={`en-${p}`} style={{ textAlign: "right", fontFamily: "monospace", background: v >= 0 ? "#6a1b9a" : "#880e4f", color: "white" }}>{fmt(v)}</td>;
                })}
                <td style={{ textAlign: "right", fontFamily: "monospace", background: (totalsBySezione.netto_ext.tot >= 0 ? "#4a148c" : "#b71c1c") }}>{fmt(totalsBySezione.netto_ext.tot)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {movimentiConto && (
        <MovimentiContoModal leaf={movimentiConto} dataDal={dataDal} dataAl={dataAl} onClose={() => setMovimentiConto(null)} />
      )}
    </div>
  );
}

function RenderNode({ level, keyPath, node, open, onToggle, tFat, tExt, fallbackLabel, children }) {
  const styles = [
    { bg: "#0d47a1", fg: "white", pad: 8 },
    { bg: "#90caf9", fg: "#0d47a1", pad: 32 },
    { bg: "#ffffff", fg: "#263238", pad: 50 },
  ][level];
  const empty = (!tFat || tFat.tot === 0) && (!tExt || tExt.tot === 0);
  const idc = node.node?.id_conto;
  const code = node.node ? nodeCode(node.node) : fallbackLabel;
  const label = node.node ? node.node.descrizione : fallbackLabel;
  const content = (
    <>
      <span style={{ display: "inline-block", width: 14 }}>{open ? "▼" : "▶"}</span>
      <span style={{ fontFamily: "monospace", opacity: 0.85, marginRight: 6 }}>{code}</span>
      <span>{label}</span>
    </>
  );
  return (
    <>
      <tr onClick={onToggle} data-testid={`pn-bilancio-node-${keyPath.replace(/\//g, "-")}`}
        style={{ background: styles.bg, color: styles.fg, fontWeight: 700, cursor: "pointer" }}>
        <td style={{ textAlign: "center", color: styles.fg, background: styles.bg, fontFamily: "monospace", opacity: 0.9, fontWeight: 700 }}>{""}</td>
        <td style={{ padding: `${level === 0 ? 8 : 6}px 10px ${level === 0 ? 8 : 6}px ${styles.pad}px`, color: styles.fg, background: styles.bg, fontWeight: 700 }}>{content}</td>
        {PLESSI_COLS.map(p => (
          <td key={`f-${p}`} style={{ textAlign: "right", fontFamily: "monospace", color: styles.fg, background: styles.bg, opacity: empty ? 0.5 : 1, fontWeight: 700 }}>
            {tFat && tFat[p] ? fmt(tFat[p]) : "—"}
          </td>
        ))}
        <td style={{ textAlign: "right", fontFamily: "monospace", color: styles.fg, fontWeight: 800, background: level === 0 ? "#073275" : "rgba(0,0,0,0.15)", borderRight: "3px solid rgba(255,255,255,0.5)" }}>
          {tFat && tFat.tot ? fmt(tFat.tot) : "—"}
        </td>
        {PLESSI_COLS.map(p => (
          <td key={`e-${p}`} style={{ textAlign: "right", fontFamily: "monospace", color: styles.fg, background: styles.bg, opacity: empty ? 0.5 : 1, fontWeight: 700 }}>
            {tExt && tExt[p] ? fmt(tExt[p]) : "—"}
          </td>
        ))}
        <td style={{ textAlign: "right", fontFamily: "monospace", color: styles.fg, fontWeight: 800, background: level === 0 ? "#4a148c" : "rgba(0,0,0,0.15)" }}>
          {tExt && tExt.tot ? fmt(tExt.tot) : "—"}
        </td>
      </tr>
      {children}
    </>
  );
}

const FAT_COL = "#0d47a1";
const EXT_COL = "#6a1b9a";

function LeafRow({ leaf, pivotByConto, indent, onDelete, mapping, dataDal, dataAl, onOpenMovimenti }) {
  const v = pivotByConto[leaf.id_conto];
  const fat = v?.fatture || EMPTY();
  const ext = v?.extra || EMPTY();
  const empty = fat.tot === 0 && ext.tot === 0;
  const causali = mapping?.[leaf.id_conto] || [];
  return (
    <tr data-testid={`pn-bilancio-leaf-${leaf.id_conto}`}>
      <td style={{ textAlign: "center", fontFamily: "monospace", color: empty ? "#bbb" : FAT_COL, fontWeight: 700 }}>{leaf.id_conto}</td>
      <td style={{ paddingLeft: indent, color: empty ? "#aaa" : "#263238", position: "relative", paddingRight: 110 }}>
        <span style={{ fontFamily: "monospace", color: "#888", marginRight: 6 }}>{leaf.c0}.{leaf.c1 ?? "-"}.{leaf.c3 ?? "-"}</span>
        {leaf.descrizione}
        {causali.length > 0 && (
          <span style={{ marginLeft: 10, padding: "2px 8px", background: "#e8f5e9", color: "#1b5e20", border: "1px solid #4caf50", fontSize: 10, fontWeight: 700, borderRadius: 3 }}
            title={`Causale Movimenti collegata via Mapping Ricavi: ${causali.map(c => `[${c.cau_code}] ${c.cau_des}`).join(", ")}`}>
            🔗 {causali.length}
          </span>
        )}
        {!empty && (
          <button onClick={() => onOpenMovimenti(leaf)} data-testid={`pn-bil-mov-${leaf.id_conto}`}
            title={`Mostra movimenti del conto "${leaf.descrizione}" dal ${dataDal} al ${dataAl}`}
            style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", padding: "3px 10px", background: "#0d47a1", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 3, letterSpacing: 0.3, width: 96, textAlign: "center" }}>
            📋 Movimenti
          </button>
        )}
      </td>
      {PLESSI_COLS.map(p => (
        <td key={`f-${p}`} style={{ textAlign: "right", fontFamily: "monospace", color: fat[p] ? "#263238" : "#ddd" }}>
          {fat[p] ? fmt(fat[p]) : "—"}
        </td>
      ))}
      <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, background: "#ffffff", color: fat.tot ? FAT_COL : "#bbb", borderRight: "3px solid #ccc" }}>
        {fat.tot ? fmt(fat.tot) : "—"}
      </td>
      {PLESSI_COLS.map(p => (
        <td key={`e-${p}`} style={{ textAlign: "right", fontFamily: "monospace", color: ext[p] ? "#263238" : "#ddd" }}>
          {ext[p] ? fmt(ext[p]) : "—"}
        </td>
      ))}
      <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, background: "#ffffff", color: ext.tot ? EXT_COL : "#bbb" }}>
        {ext.tot ? fmt(ext.tot) : "—"}
      </td>
    </tr>
  );
}


function MovimentiContoModal({ leaf, dataDal, dataAl, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🆕 Feb 2026: raggruppamento banca (plesso o corso/causale) + stampa
  const [bancaGroupBy, setBancaGroupBy] = useState("plesso");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/pn/report/movimenti-conto", {
          params: { id_conto: leaf.id_conto, data_dal: dataDal || undefined, data_al: dataAl || undefined },
        });
        if (alive) setData(r.data);
      } catch {
        if (alive) setData({ items: [], count: 0, totale: 0 });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [leaf.id_conto, dataDal, dataAl]);

  return (
    <div onClick={onClose} data-testid="pn-bil-mov-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="pn-bil-mov-modal"], [data-testid="pn-bil-mov-modal"] * { visibility: visible; }
          [data-testid="pn-bil-mov-modal"] { position: absolute; inset: 0; background: white !important; padding: 0; overflow: visible !important; height: auto !important; max-height: none !important; }
          [data-testid="pn-bil-mov-modal"] > div { box-shadow: none !important; border: none !important; max-height: none !important; width: 100% !important; }
          .pn-mov-hide-on-print { display: none !important; }
          .pn-mov-print-table { font-size: 10pt !important; }
          .pn-mov-print-table tr { break-inside: avoid; }
        }
      `}</style>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", width: "min(1200px, 95vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 30px rgba(0,0,0,0.4)", border: "2px solid #0d47a1" }}>
        <div style={{ background: "#0d47a1", color: "white", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.5, textTransform: "uppercase" }}>Movimenti conto</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>[{leaf.id_conto}] {leaf.descrizione}</div>
            <div style={{ fontSize: 11, opacity: 0.9 }}>Periodo: {dataDal || "—"} → {dataAl || "—"}</div>
          </div>
          <button onClick={onClose} data-testid="pn-bil-mov-close"
            style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
          {loading && <div style={{ padding: 40, textAlign: "center", color: "#666" }}>Caricamento…</div>}
          {!loading && data && data.count === 0 && (data.banca_count || 0) === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Nessun movimento per questo conto nel periodo selezionato.</div>
          )}
          {!loading && data && (data.count > 0 || (data.banca_count || 0) > 0) && (
            <>
              <div style={{ padding: "8px 10px", background: "#e3f2fd", border: "1px solid #90caf9", marginBottom: 10, display: "flex", gap: 20, fontSize: 12, fontWeight: 700, flexWrap: "wrap" }}>
                <span>Righe PN: <b>{data.count}</b></span>
                <span style={{ color: "#1b5e20" }}>Costi (+): <b>{fmt(data.totale_positivi)}</b></span>
                <span style={{ color: "#c62828" }}>Storni (−): <b>{fmt(data.totale_negativi)}</b></span>
                <span style={{ color: "#0d47a1" }}>Subtot PN: <b>€ {fmt(data.totale)}</b></span>
                {(data.banca_count || 0) > 0 && (
                  <>
                    <span style={{ color: "#e65100", marginLeft: 12 }}>Banca: <b>{data.banca_count}</b></span>
                    <span style={{ color: "#1b5e20" }}>Subtot Banca: <b>€ {fmt(data.banca_totale)}</b></span>
                  </>
                )}
                <span style={{ marginLeft: "auto", color: "#37474f", fontSize: 13 }}>TOT GENERALE: <b>€ {fmt((data.totale || 0) + (data.banca_totale || 0))}</b></span>
              </div>
              {data.count > 0 && (() => {
                // Raggruppa per FATTURA (id_fattura). Dentro ogni fattura, raggruppa per SETTORE (plesso).
                const dmyKey = (s) => (s || "").split("/").reverse().join("");
                const fatGroups = {};
                data.items.forEach(it => {
                  const key = it.id_fattura ?? `nofat-${it.prot || Math.random()}`;
                  if (!fatGroups[key]) {
                    fatGroups[key] = {
                      id_fattura: it.id_fattura,
                      prot: it.prot,
                      prot_tipo_id: it.prot_tipo_id,
                      tipo_label: it.tipo_label,
                      data_registrazione: it.data_registrazione,
                      data_fattura: it.data_fattura,
                      numero_fattura: it.numero_fattura,
                      fornitore: it.fornitore,
                      descrizione_extra: it.descrizione_extra,
                      rows: [],
                    };
                  }
                  fatGroups[key].rows.push(it);
                });
                // Ordina le fatture per protocollo crescente (data come tie-break)
                const fatList = Object.values(fatGroups).sort((a, b) => {
                  const pa = String(a.prot || "").padStart(20, "0");
                  const pb = String(b.prot || "").padStart(20, "0");
                  if (pa !== pb) return pa.localeCompare(pb);
                  return dmyKey(a.data_registrazione).localeCompare(dmyKey(b.data_registrazione));
                });
                return (
                  <table className="sw-table" style={{ fontSize: 11 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>Data Reg.</th>
                        <th style={{ width: 110 }}>Settore</th>
                        <th style={{ width: 110 }}>N° Fattura</th>
                        <th>Fornitore / Descrizione</th>
                        <th style={{ width: 110, textAlign: "right" }}>Importo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fatList.map((fat) => {
                        // Sotto-raggruppa per plesso
                        const plesGroups = {};
                        fat.rows.forEach(rw => {
                          const p = rw.plesso || "(non specificato)";
                          if (!plesGroups[p]) plesGroups[p] = [];
                          plesGroups[p].push(rw);
                        });
                        const plessiOrd = Object.keys(plesGroups).sort();
                        const totFatPos = fat.rows.filter(x => x.importo_signed >= 0).reduce((s, x) => s + x.importo_signed, 0);
                        const totFatNeg = fat.rows.filter(x => x.importo_signed < 0).reduce((s, x) => s + x.importo_signed, 0);
                        const totFat = totFatPos + totFatNeg;
                        const key = fat.id_fattura ?? fat.prot;
                        return (
                          <React.Fragment key={`fat-${key}`}>
                            {/* Header della fattura */}
                            <tr>
                              <td colSpan={5} style={{ background: "#0d47a1", color: "white", fontWeight: 800, padding: "6px 10px", letterSpacing: 0.3, fontSize: 12 }}>
                                <span style={{
                                  display: "inline-block", padding: "1px 6px", marginRight: 8, fontSize: 10, fontWeight: 700, color: "white",
                                  background: fat.prot_tipo_id === 3 ? EXT_COL : (fat.prot_tipo_id === 2 ? "#c62828" : FAT_COL),
                                }}>{fat.tipo_label}</span>
                                <span style={{ fontFamily: "monospace", marginRight: 10 }}>📄 {fat.prot}</span>
                                <span style={{ opacity: 0.95 }}>{fat.fornitore || fat.descrizione_extra || "—"}</span>
                                {fat.numero_fattura && <span style={{ marginLeft: 12, opacity: 0.75, fontWeight: 400, fontSize: 11 }}>· N° {fat.numero_fattura}</span>}
                                <span style={{ marginLeft: 12, opacity: 0.75, fontWeight: 400, fontFamily: "monospace", fontSize: 11 }}>· Reg. {fat.data_registrazione}</span>
                                {fat.prot_tipo_id === 3 && fat.id_fattura && (
                                  <button onClick={async () => {
                                    if (!window.confirm(`Eliminare l'Operazione Extra ${fat.prot} ("${fat.descrizione_extra || ''}")?\n\nVerranno cancellate la testata e tutte le sue righe contabili.`)) return;
                                    try {
                                      await api.delete(`/pn/fatture/${fat.id_fattura}`);
                                      alert("✓ Eliminata. Ricarica il popup per aggiornare i dati.");
                                    } catch (e) {
                                      alert("Errore: " + (e.response?.data?.detail || e.message));
                                    }
                                  }} title="Elimina questa Operazione Extra"
                                    style={{ marginLeft: 10, padding: "1px 6px", fontSize: 10, background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.6)", cursor: "pointer" }}>✕</button>
                                )}
                              </td>
                            </tr>
                            {/* Righe divise per plesso */}
                            {plessiOrd.map(pl => {
                              const arr = plesGroups[pl];
                              const subPles = arr.reduce((s, x) => s + x.importo_signed, 0);
                              return (
                                <React.Fragment key={`fat-${key}-pl-${pl}`}>
                                  {arr.map((it, i) => (
                                    <tr key={`fat-${key}-${pl}-${i}`} data-testid={`pn-bil-mov-row-${key}-${pl}-${i}`}>
                                      <td style={{ fontFamily: "monospace", paddingLeft: 16 }}>{it.data_registrazione || it.data_fattura}</td>
                                      <td style={{ paddingLeft: 16, fontWeight: 700, color: "#37474f" }}>📍 {pl}</td>
                                      <td>{it.numero_fattura || "—"}</td>
                                      <td style={{ color: "#666", fontStyle: "italic" }}>{arr.length > 1 ? `(riga ${i + 1}/${arr.length})` : ""}</td>
                                      <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: it.importo_signed < 0 ? "#c62828" : "#1b5e20" }}>
                                        {fmt(it.importo_signed)}
                                      </td>
                                    </tr>
                                  ))}
                                  {arr.length > 1 && (
                                    <tr>
                                      <td colSpan={4} style={{ background: "#f5f5f5", textAlign: "right", fontWeight: 700, padding: "3px 16px", fontSize: 10, color: "#37474f", fontStyle: "italic" }}>
                                        Subtotale settore {pl}:
                                      </td>
                                      <td style={{ background: "#f5f5f5", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 11, color: subPles < 0 ? "#c62828" : "#1b5e20" }}>
                                        € {fmt(subPles)}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                            {/* Totale fattura */}
                            <tr>
                              <td colSpan={4} style={{ background: "#e3f2fd", textAlign: "right", fontWeight: 800, padding: "5px 10px", fontSize: 11, color: "#0d47a1" }}>
                                Totale fattura {fat.prot}{totFatNeg < 0 ? ` (Costi: ${fmt(totFatPos)} · Storni: ${fmt(totFatNeg)})` : ""}:
                              </td>
                              <td style={{ background: "#e3f2fd", textAlign: "right", fontFamily: "monospace", fontWeight: 900, fontSize: 13, color: totFat < 0 ? "#c62828" : "#0d47a1" }}>
                                € {fmt(totFat)}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}

              {(() => {
                const banca = data.banca_count || 0;
                const causali = data.banca_causali || [];
                return (
                  <>
                    <div style={{ marginTop: 24, marginBottom: 8, padding: "8px 12px", background: banca ? "#fff3e0" : "#fafafa", border: `1px solid ${banca ? "#ef6c00" : "#bbb"}`, display: "flex", gap: 16, fontSize: 12, fontWeight: 700, alignItems: "center", flexWrap: "wrap" }} className="pn-mov-banca-header">
                      <span style={{ color: banca ? "#e65100" : "#666" }}>🏦 Movimenti Segremat (Addebiti famiglie) — via Mapping ({banca})</span>
                      {banca > 0 && (
                        <>
                          <span data-testid="pn-mov-group-by" style={{ display: "inline-flex", gap: 4, alignItems: "center", marginLeft: 12 }} className="pn-mov-hide-on-print">
                            <span style={{ fontSize: 11, color: "#5d4037", fontWeight: 600 }}>Raggruppa per:</span>
                            <button onClick={() => setBancaGroupBy("plesso")} data-testid="pn-mov-group-plesso"
                              style={{ padding: "3px 10px", background: bancaGroupBy === "plesso" ? "#ef6c00" : "white", color: bancaGroupBy === "plesso" ? "white" : "#ef6c00", border: "1px solid #ef6c00", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Plesso</button>
                            <button onClick={() => setBancaGroupBy("corso")} data-testid="pn-mov-group-corso"
                              style={{ padding: "3px 10px", background: bancaGroupBy === "corso" ? "#ef6c00" : "white", color: bancaGroupBy === "corso" ? "white" : "#ef6c00", border: "1px solid #ef6c00", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Corso/Causale</button>
                          </span>
                          <button
                            data-testid="pn-mov-print-btn"
                            onClick={() => window.print()}
                            className="pn-mov-hide-on-print"
                            title="Stampa questa lista (Ctrl+P equivalente)"
                            style={{ padding: "3px 12px", background: "#0d47a1", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", marginLeft: 8 }}
                          >
                            🖨 Stampa
                          </button>
                        </>
                      )}
                      {banca > 0 && <span style={{ color: "#1b5e20", marginLeft: "auto" }}>Totale: <b>€ {fmt(data.banca_totale)}</b></span>}
                    </div>
                    <div style={{ fontSize: 11, padding: "6px 10px", background: "#f5f5f5", marginBottom: 6, color: "#555" }}>
                      {causali.length === 0 ? (
                        <span style={{ color: "#c62828" }}>⚠ Nessuna causale mappata a questo conto. Per vedere i movimenti banca, vai in <b>Mapping Ricavi</b> e collega le causali pertinenti al conto {leaf.id_conto}.</span>
                      ) : (
                        <>Causali mappate: {causali.map(c => `[${c.cau_code}] ${c.cau_des}`).join(", ")}{banca === 0 && <span style={{ color: "#c62828", marginLeft: 12 }}>⚠ Nessun movimento trovato per queste causali nel periodo selezionato.</span>}</>
                      )}
                    </div>
                    {banca > 0 && (() => {
                      const groups = {};
                      const groupKeyFn = bancaGroupBy === "corso"
                        ? (it) => (it.cau_des || `Cau ${it.cau_code || ""}`).trim() || "(senza causale)"
                        : (it) => it.plesso || "(non specificato)";
                      data.banca_items.forEach(it => {
                        const p = groupKeyFn(it);
                        if (!groups[p]) groups[p] = [];
                        groups[p].push(it);
                      });
                      const dmyKey = (s) => (s || "").split("/").reverse().join("");
                      // Ordina ogni gruppo per data_pag ascendente
                      Object.values(groups).forEach(arr => arr.sort((a, b) => dmyKey(a.data_pag).localeCompare(dmyKey(b.data_pag))));
                      const gruppiOrdinati = Object.keys(groups).sort();
                      const groupIcon = bancaGroupBy === "corso" ? "🎓" : "🏦";
                      const groupLabel = bancaGroupBy === "corso" ? "Corso" : "Plesso";
                      return (
                        <table className="sw-table pn-mov-print-table" style={{ fontSize: 11 }}>
                          <thead>
                            <tr>
                              <th style={{ width: 90 }}>Data Pag.</th>
                              <th style={{ width: 70 }}>Num</th>
                              <th style={{ width: 200 }}>Alunno</th>
                              <th style={{ width: 70 }}>Classe</th>
                              <th style={{ width: 50 }}>Set</th>
                              <th>Causale</th>
                              <th style={{ width: 110, textAlign: "right" }}>Importo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gruppiOrdinati.map(gname => {
                              const arr = groups[gname];
                              const sub = arr.reduce((s, x) => s + x.importo, 0);
                              return (
                                <React.Fragment key={`b-${gname}`}>
                                  <tr><td colSpan={7} style={{ background: "#ef6c00", color: "white", fontWeight: 800, padding: "4px 10px", fontSize: 11 }}>
                                    {groupIcon} {gname} <span style={{ opacity: 0.85, fontWeight: 400, marginLeft: 8 }}>· {arr.length} mov.</span>
                                  </td></tr>
                                  {arr.map((it, i) => (
                                    <tr key={`b-${gname}-${i}`}>
                                      <td style={{ fontFamily: "monospace" }}>{it.data_pag}</td>
                                      <td>{it.numero || "—"}</td>
                                      <td>{it.alu_nome || `Alu ${it.alu_cod}`}</td>
                                      <td>{it.classe || "—"}</td>
                                      <td style={{ fontFamily: "monospace" }}>{it.setnom || "—"}</td>
                                      <td>{it.cau_des} {it.descrizione ? <span style={{ color: "#666" }}>· {it.descrizione}</span> : null}</td>
                                      <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: "#1b5e20" }}>{fmt(it.importo)}</td>
                                    </tr>
                                  ))}
                                  <tr>
                                    <td colSpan={6} style={{ background: "#fff3e0", textAlign: "right", fontWeight: 800, padding: "4px 10px", fontSize: 11 }}>Subtotale {groupLabel.toLowerCase()} {gname}:</td>
                                    <td style={{ background: "#fff3e0", textAlign: "right", fontFamily: "monospace", fontWeight: 800, color: "#1b5e20" }}>€ {fmt(sub)}</td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      );
                    })()}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

